<script lang="ts" setup>
import type { MenuItemProps, MenuItemSlots } from './';

import { useSharedState } from 'composables/useSharedState';
import { computed, useHost } from 'vue';
import { useLayout } from 'components/Layout/useLayout';
import { usePersistentRef } from 'composables/usePersistentRef';
import { menuItemClasses } from './menu.styles';
import { useMenu } from './useMenu';

defineOptions({ name: 'MenuItem' });
const props = withDefaults(defineProps<MenuItemProps>(), {
  selected: false,
  open: false,
  type: 'link',
  children: () => []
});

const emit = defineEmits<(event: 'update:open', value: boolean) => void>();

defineSlots<MenuItemSlots>();

const { toggleCollapsed, collapsed: layoutCollapsed } = useLayout();
const host = useHost();
const state = useSharedState(props);
const menu = useMenu();

// Persist opened state with position-based scoping
if (state.open && host) {
  usePersistentRef(state.open, {
    key: 'e-menu-item:open',
    scope: 'position',
    options: {
      host,
      props: {
        label: props.label,
        to: props.to,
        icon: props.icon
      }
    }
  });
}

const openedProxy = computed({
  get: () => state.open?.value ?? props.open ?? false,
  set: (v: boolean) => {
    // Emit updates the prop, which automatically updates the state via useSharedState
    emit('update:open', v);
    // Also update via controller for consistency
    if (v) {
      menu.open();
      // Expand layout if collapsed when opening
      if (layoutCollapsed.value) {
        toggleCollapsed();
      }
    } else {
      menu.close();
    }
  }
});

const isCollapsed = computed(() => state.collapsed?.value || layoutCollapsed?.value);

const itemStyles = computed(() =>
  menuItemClasses({
    type: props.type,
    active: 'false', // TODO: integrate with router
    selected: props.selected ? 'true' : 'false',
    disabled: props.disabled ? 'true' : 'false',
    isSubItem: menu.isChild.value ? 'true' : 'false',
    detailsOpen: openedProxy.value ? 'true' : 'false'
  })
);
</script>

<template>
  <li :class="[type === 'label' && itemStyles.label]">
    <a
      v-if="!menu.isExpandable.value"
      :class="[
        type !== 'label' && (menu.isChild.value ? itemStyles.submenuItem : itemStyles.base),
        state.class?.value || null
      ]"
      class="e-menu-item"
      :href="to"
      :target="target ?? undefined"
      :aria-disabled="disabled"
      :selected="selected ? '' : null"
    >
      <div
        :class="itemStyles.icon"
        class="e-menu-icon"
      >
        <slot name="leading">
          <e-icon
            v-if="icon && type !== 'label'"
            :size="menu.isChild.value ? 'xs' : 'md'"
            :name="icon"
          ></e-icon>
        </slot>
      </div>

      <div
        v-if="!isCollapsed"
        class="contents"
      >
        <slot>
          <span
            :class="itemStyles.title"
            class="e-menu-item-title"
          >
            {{ state.label?.value }}
          </span>
        </slot>
      </div>

      <slot name="trailing">
        <div
          v-if="state.target?.value === '_blank' && !isCollapsed"
          :class="itemStyles.icon"
          class="e-menu-icon"
        >
          <e-icon
            :class="itemStyles.detailsTrailingIcon"
            class="e-menu-item-details-trailing-icon"
            name="open-in-new"
          ></e-icon>
        </div>
      </slot>
    </a>

    <details
      v-show="menu.isExpandable.value"
      :class="itemStyles.details"
      class="e-menu-item-details"
      :open="openedProxy && !isCollapsed"
      @toggle="openedProxy = ($event.target as HTMLDetailsElement).open"
    >
      <summary
        :class="itemStyles.detailsSummary"
        class="e-menu-item-details-summary"
      >
        <span
          :class="[menu.isChild.value ? itemStyles.submenuItem : itemStyles.base]"
          class="e-menu-item"
          :selected="selected ? '' : null"
        >
          <div
            :class="itemStyles.icon"
            class="e-menu-icon"
          >
            <slot name="leading">
              <e-icon
                v-if="icon && type !== 'label'"
                :size="menu.isChild.value ? 'xs' : 'md'"
                :name="icon"
              ></e-icon>
            </slot>
          </div>

          <div
            v-show="!isCollapsed"
            class="contents"
          >
            <slot>
              <span
                :class="itemStyles.title"
                class="e-menu-item-title"
              >
                {{ state.label?.value }}
              </span>
            </slot>
          </div>

          <e-icon
            v-if="state.target?.value === '_blank' && !isCollapsed && type !== 'label'"
            :class="itemStyles.detailsTrailingIcon"
            class="e-menu-icon e-menu-item-details-trailing-icon"
            name="open-in-new"
          ></e-icon>
          <e-icon
            v-else-if="!isCollapsed && type !== 'label'"
            :class="itemStyles.detailsTrailingIcon"
            class="e-menu-icon e-menu-item-details-trailing-icon"
            name="chevron-right"
          ></e-icon>
        </span>
      </summary>
      <ul
        v-show="menu.hasChildren.value && !isCollapsed"
        :class="itemStyles.detailsContent"
        class="e-menu-item-details-content"
      >
        <slot name="children">
          <e-menu-item
            v-for="(child, index) in children"
            :key="index"
            v-bind="child"
          />
        </slot>
      </ul>
    </details>
  </li>
</template>
